// CAPTURES EN ATTENTE D'ENVOI — gardées dans le téléphone (IndexedDB).
//
// Sur le terrain, le réseau tombe. Un entretien enregistré ne doit pas se
// perdre parce que l'envoi a échoué : l'enregistrement est écrit ici pendant
// qu'on enregistre, puis effacé dès que le serveur l'a reçu. Rien d'autre n'est
// stocké, et rien ne part ailleurs que vers notre API.
//
// Stockage strictement nécessaire à la fonction demandée (enregistrer), pas
// une préférence : il ne dépend pas du bandeau cookies.

export interface PendingCapture {
  blob: Blob;
  /** Type MIME sans paramètres (`audio/webm`), celui qu'accepte le serveur. */
  type: string;
  name: string;
  durationMs: number;
  savedAt: number;
}

const DB = 'zygosight-capture';
const STORE = 'pending';

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function run<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await open();
  try {
    return await new Promise<T>((resolve, reject) => {
      const req = fn(db.transaction(STORE, mode).objectStore(STORE));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  } finally {
    db.close();
  }
}

/** Écrit (ou remplace) la capture en attente pour `key` (un entretien). */
export async function savePending(key: string, capture: PendingCapture): Promise<void> {
  try {
    await run('readwrite', (s) => s.put(capture, key));
  } catch {
    /* stockage indisponible (navigation privée) : l'envoi immédiat reste possible */
  }
}

export async function loadPending(key: string): Promise<PendingCapture | null> {
  try {
    return ((await run('readonly', (s) => s.get(key))) as PendingCapture | undefined) ?? null;
  } catch {
    return null;
  }
}

export async function clearPending(key: string): Promise<void> {
  try {
    await run('readwrite', (s) => s.delete(key));
  } catch {
    /* rien à effacer */
  }
}
